import { Target, Eye, Award, TrendingUp, Users, Shield, Lightbulb, BarChart3, Zap, Linkedin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import backgroundNoise from "@/assets/background-noise.svg";
import { useState } from "react";
import valueGrowth from "@/assets/value-growth.jpg";
import valuePartnership from "@/assets/value-partnership.jpg";
import valueTransparency from "@/assets/value-transparency.jpg";
import valueCreative from "@/assets/value-creative.jpg";
import valueData from "@/assets/value-data.jpg";
import valuePerformance from "@/assets/value-performance.jpg";
import founderImage from "@/assets/founder-sandra.jpg";

const About = () => {
  const heroReveal = useScrollReveal();
  const whoWeAreReveal = useScrollReveal();
  const visionReveal = useScrollReveal();
  const valuesReveal = useScrollReveal();
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const values = [
    {
      icon: TrendingUp,
      image: valueGrowth,
      title: "Growth-Focused Strategy",
      description: "At Northquest, growth is at the heart of every strategy we craft. Each marketing plan is tailored to your unique goals, ensuring every decision drives tangible, measurable impact, not just superficial numbers."
    },
    {
      icon: Users,
      image: valuePartnership,
      title: "Client-Centered Partnership",
      description: "Our approach goes beyond delivering projects; we build lasting relationships rooted in trust, collaboration, and a deep understanding of your business objectives."
    },
    {
      icon: Shield,
      image: valueTransparency,
      title: "Transparency & Accountability",
      description: "We operate with full honesty in every interaction. From transparent reporting to open communication, we hold ourselves accountable for delivering real results that matter."
    },
    {
      icon: Lightbulb,
      image: valueCreative,
      title: "Creative Ideas",
      description: "We combine bold, original thinking with cutting-edge digital tools and data insights to create marketing strategies that make your brand stand out."
    },
    {
      icon: BarChart3,
      image: valueData,
      title: "Data-Driven Execution",
      description: "Data guides everything we do. From precise audience targeting to continuous optimization, every campaign is grounded in solid analysis to maximize performance."
    },
    {
      icon: Zap,
      image: valuePerformance,
      title: "High Performance",
      description: "High performance isn't an exception; it's our standard. From the solutions we design to the strategies we execute, every part of our work is built to deliver impact."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[70vh] flex items-center justify-center px-4 pt-24 md:pt-32 pb-12 md:pb-20">
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              About <span className="text-accent italic">Northquest Marketing</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8 md:mb-12">
              We Empower Visionary Brands to Grow with Purpose and Thrive with Impact.
            </p>
            
            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed text-left max-w-4xl mx-auto">
              <p>
                We are a digital marketing consultancy that empowers visionary brands to grow with purpose and thrive with impact. Guided by values of growth, creativity, collaboration, and integrity, we design strategies that deliver measurable results while strengthening businesses, uplifting communities, and protecting the planet.
              </p>
              <p>
                Our clients are forward-thinking leaders who want more than growth for its own sake. They seek lasting impact, stronger brand reputations, loyal customers, and meaningful marketing communication. We partner with them at every step, blending creativity, technology, and data-driven insight to unlock opportunities for sustainable success.
              </p>
              <p>
                More than campaigns, we build confidence and capability, ensuring our clients continue to thrive long after our work is done. Together, we shape a future for businesses where growth is faster, smarter, fairer, and truly meaningful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll-Based Gradient Transition */}
      <div className="relative h-[40vh]">
        <div className="sticky top-0 h-screen w-full">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-foreground/50 to-foreground"></div>
        </div>
      </div>

      {/* Vision, Mission, Values */}
      <section 
        className="py-12 md:py-20 px-4 bg-foreground text-background relative overflow-hidden -mt-[40vh]"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={visionReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            visionReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-6xl mx-auto mb-12 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4 text-center">Our Foundation</h2>
            <p className="text-center text-background/70 text-base md:text-lg mb-8 md:mb-16 max-w-3xl mx-auto px-4">
              Built on a clear vision, purposeful mission, and strong values that guide every decision we make.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              {/* Vision */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-10 border-2 border-white/20 transition-all duration-500 hover:border-white/40 group">
                <div className="flex flex-col">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 border-2 border-white/20 group-hover:bg-white group-hover:border-white group-hover:scale-110 transition-all duration-300">
                    <Eye size={32} className="md:w-10 md:h-10 text-background group-hover:text-foreground transition-colors" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">Vision</h3>
                  <p className="text-background/80 leading-relaxed text-sm md:text-base">
                    To empower visionary brands to grow sustainably and lead their industries through innovative, high-impact digital marketing. By 2035, we aim to help 2,000 ambitious brands grow sustainably as industry leaders.
                  </p>
                </div>
              </div>

              {/* Mission */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-10 border-2 border-white/20 transition-all duration-500 hover:border-white/40 group">
                <div className="flex flex-col">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 border-2 border-white/20 group-hover:bg-white group-hover:border-white group-hover:scale-110 transition-all duration-300">
                    <Target size={32} className="md:w-10 md:h-10 text-background group-hover:text-foreground transition-colors" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">Mission</h3>
                  <p className="text-background/80 leading-relaxed text-sm md:text-base">
                    Our mission is to streamline digital marketing for visionary brands so they can grow with purpose, innovate with confidence, and thrive with impact.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Founder Section */}
          <div className="max-w-5xl mx-auto mb-12 md:mb-20">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border-2 border-white/20 hover:border-white/30 transition-all duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                {/* Photo Section */}
                <div className="lg:col-span-2 relative h-64 lg:h-auto">
                  <img 
                    src={founderImage} 
                    alt="Sandra Akello - Founder" 
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-foreground/40 via-transparent to-transparent"></div>
                </div>
                
                {/* Content Section */}
                <div className="lg:col-span-3 p-6 md:p-10 lg:p-12">
                  <div className="mb-4">
                    <p className="text-accent text-sm md:text-base font-semibold mb-2">Leadership</p>
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">Sandra Akello</h3>
                    <p className="text-background/60 text-sm md:text-base">Founder & Chief Strategist</p>
                  </div>
                  
                  <div className="space-y-4 text-background/80 text-sm md:text-base leading-relaxed mb-6">
                    <p>
                      Sandra Akello embodies Northquest's vision of purposeful growth and meaningful collaboration. With a strong academic foundation in Digital Marketing and a proven record of strategic execution, she combines insight with action to help businesses thrive.
                    </p>
                    <p>
                      Her leadership goes beyond transactions—she is dedicated to creating meaningful collaborations that drive business success while contributing positively to people, communities, and the planet.
                    </p>
                    <p>
                      Sandra's approach balances strategic thinking with creative innovation, always focusing on building partnerships that deliver measurable results and lasting value for clients and stakeholders alike.
                    </p>
                  </div>
                  
                  {/* Contact Links */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                    <a
                      href="https://www.linkedin.com/in/sandra-akello-ba8788177/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-lg transition-all duration-300 text-sm group"
                    >
                      <Linkedin size={18} className="text-background/70 group-hover:text-accent transition-colors" />
                      <span className="text-background/80 group-hover:text-background transition-colors">Connect on LinkedIn</span>
                    </a>
                    <a
                      href="mailto:sandra@northquestmarketing.co.uk"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-lg transition-all duration-300 text-sm group"
                    >
                      <Mail size={18} className="text-background/70 group-hover:text-accent transition-colors" />
                      <span className="text-background/80 group-hover:text-background transition-colors">Send Email</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Values */}
          <div 
            ref={valuesReveal.ref}
            className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
              valuesReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="text-center mb-12 md:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">Our Core Values</h2>
              <p className="text-background/70 text-base md:text-lg max-w-2xl mx-auto px-4">
                Six principles that drive everything we do and guide how we work with our clients.
              </p>
            </div>
            
            <div className="space-y-0">
              {values.map((value, index) => {
                // Different positions for each card
                const positions = [
                  '-top-32 left-1/4 -rotate-6',      // 01 - top left, tilt left
                  '-top-32 right-1/4 rotate-6',      // 02 - top right, tilt right
                  '-top-28 left-1/3 -rotate-12',     // 03 - top left-center, more tilt
                  '-top-28 right-1/3 rotate-12',     // 04 - top right-center, more tilt
                  '-top-36 left-1/2 -translate-x-1/2 -rotate-3', // 05 - top center, slight tilt
                  '-top-32 right-1/4 rotate-8'       // 06 - top right, medium tilt
                ];
                
                return (
                  <div 
                    key={index} 
                    className="group relative border-t border-white/10 first:border-t-0 py-8 md:py-12 transition-all duration-500 px-4 md:px-8 -mx-4 md:-mx-8 cursor-pointer"
                    onMouseEnter={() => setHoveredValue(index)}
                    onMouseLeave={() => setHoveredValue(null)}
                  >
                    {/* Content */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start">
                      {/* Number */}
                      <div className="md:col-span-2">
                        <div className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-accent/20 group-hover:text-accent/60 transition-colors duration-500 leading-none">
                          0{index + 1}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="md:col-span-10">
                        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 transition-colors duration-300">
                          {value.title}
                        </h3>
                        <p className="text-background/70 text-base md:text-lg leading-relaxed max-w-3xl transition-colors duration-300">
                          {value.description}
                        </p>
                      </div>
                    </div>

                    {/* Hover Card - unique position for each - hidden on mobile */}
                    <div className={`hidden md:block absolute ${positions[index]} w-64 h-48 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30 transition-all duration-700 z-20 ${
                      hoveredValue === index 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-90 pointer-events-none'
                    }`}>
                      <img
                        src={value.image}
                        alt={value.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-12 md:py-20 px-4 bg-background">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 md:mb-8">Our Leadership Philosophy</h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4 md:mb-6">
              At Northquest Marketing, leadership is defined by clarity of vision, purposeful strategy, and measurable impact. We guide ambitious businesses through the digital landscape with a balance of strategic insight and creative innovation, ensuring every decision drives real growth and lasting value.
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 md:mb-12">
              Our philosophy is rooted in empowering clients, fostering sustainable progress, and embedding ethical, inclusive, and socially responsible practices that benefit both business and community. Leadership, to us, is not only about direction but also about building trust, accountability, and partnerships that endure.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Target size={24} className="md:w-7 md:h-7 text-accent-foreground" />
                </div>
                <h3 className="font-semibold mb-2 text-base md:text-lg">Strategic Vision</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Clear direction with measurable outcomes
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="md:w-7 md:h-7 text-accent-foreground" />
                </div>
                <h3 className="font-semibold mb-2 text-base md:text-lg">Client Empowerment</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Building confidence and capability
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Award size={24} className="md:w-7 md:h-7 text-accent-foreground" />
                </div>
                <h3 className="font-semibold mb-2 text-base md:text-lg">Ethical Excellence</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Trust, accountability, and integrity
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership in Action */}
      <section className="py-12 md:py-20 px-4 bg-background">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 md:mb-12 text-center">Leadership in Action</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-lg md:text-xl font-semibold mb-3">Strategic Execution</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Every decision is guided by clear objectives and measurable outcomes, ensuring strategies deliver tangible results and sustainable growth.
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-lg md:text-xl font-semibold mb-3">Collaborative Approach</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Building trust through open communication, transparency, and genuine partnership with clients, team members, and stakeholders.
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-lg md:text-xl font-semibold mb-3">Innovation & Adaptability</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Staying ahead of trends and technologies while remaining flexible to meet evolving market demands and client needs.
                </p>
              </div>

              <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-lg md:text-xl font-semibold mb-3">Social Responsibility</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Leading with purpose, ensuring business success contributes positively to communities, society, and environmental sustainability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 px-4 bg-background relative overflow-hidden">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Ready to Transform Your <span className="text-accent italic">Digital Presence?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto px-4">
              Let's build strategies that drive real growth, create meaningful impact, and position your brand as an industry leader.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-12 md:mb-16 px-4">
              <ScheduleConsultationDialog
                buttonText="Schedule a Consultation"
                buttonClassName="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <button className="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                  Schedule a Consultation
                </button>
              </ScheduleConsultationDialog>
              <Link to="/services">
                <button className="px-6 md:px-8 py-3 md:py-4 border-2 border-foreground/20 hover:bg-foreground/5 rounded-full text-base md:text-lg font-semibold transition-all">
                  View Our Services
                </button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 pt-8 md:pt-12 border-t border-foreground/20">
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">15+</div>
                <p className="text-muted-foreground text-sm md:text-base">Years of Excellence</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">200+</div>
                <p className="text-muted-foreground text-sm md:text-base">Brands Empowered</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">98%</div>
                <p className="text-muted-foreground text-sm md:text-base">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
